'use client';

import React, { useRef, useMemo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import Image from 'next/image';
import { ProductWorkflowSection, WorkflowStep } from '@/types/sanity';
import { urlFor } from '@/sanity/lib/image';
import { CTAButton } from '@/components/cta/CTAButton';
import { cn } from '@/lib/utils';

interface WorkflowParallaxProps {
  section: ProductWorkflowSection;
  pageSlug?: string;
}

function ParallaxStep({
  step,
  index,
  imageStyle = 'circular',
}: {
  step: WorkflowStep;
  index: number;
  imageStyle?: 'circular' | 'landscape';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const contentY = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const imageY = useTransform(scrollYProgress, [0, 1], [120, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.5, 0.8, 1], [0, 1, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.9, 1, 1, 0.95]);
  const grayscale = useTransform(scrollYProgress, [0, 0.3, 0.5], [1, 0.5, 0]);
  const grayscaleFilter = useTransform(grayscale, (v) => `grayscale(${v * 100}%)`);

  const isEven = index % 2 === 0;

  return (
    <div ref={ref} className="relative md:min-h-[40vh] flex items-center">
      {/* Mobile layout: stacked */}
      <div className="w-full md:hidden">
        <div className="flex items-start gap-4">
          {/* Mobile step indicator */}
          <div className="flex flex-col items-center shrink-0 pt-1">
            <div className="h-3 w-3 rounded-full bg-primary ring-2 ring-background shadow-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-3xl font-extralight text-primary/15 leading-none tracking-tight">
              {String(step.stepNumber).padStart(2, '0')}
            </span>
            <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
            {step.image?.asset && (
              <div
                className={cn(
                  'overflow-hidden shadow-lg shadow-primary/10 ring-1 ring-border/50 mb-3',
                  imageStyle === 'landscape'
                    ? 'w-full aspect-video rounded-lg'
                    : 'w-32 h-32 rounded-full'
                )}
              >
                <Image
                  src={
                    imageStyle === 'landscape'
                      ? urlFor(step.image).width(640).height(360).quality(80).url()
                      : urlFor(step.image).width(300).height(300).quality(80).url()
                  }
                  alt={step.image?.alt || step.title}
                  width={imageStyle === 'landscape' ? 640 : 300}
                  height={imageStyle === 'landscape' ? 360 : 300}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {step.description && (
              <div className="prose prose-sm max-w-none text-muted-foreground">
                <PortableText value={step.description} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop layout: parallax timeline */}
      <motion.div
        style={{ opacity, scale }}
        className={`relative w-full hidden md:grid md:grid-cols-[1fr_auto_1fr] gap-12 items-center ${isEven ? '' : 'md:[direction:rtl]'}`}
      >
        {/* Content side */}
        <motion.div style={{ y: contentY }} className="md:text-left md:[direction:ltr]">
          <span className="inline-block text-7xl font-extralight text-primary/15 leading-none mb-2 tracking-tight">
            {String(step.stepNumber).padStart(2, '0')}
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3">{step.title}</h3>
          {step.description && (
            <div className="prose prose-lg max-w-md inline-block text-muted-foreground">
              <PortableText value={step.description} />
            </div>
          )}
        </motion.div>

        {/* Center node */}
        <div className="flex flex-col items-center">
          <div className="h-4 w-4 rounded-full bg-primary ring-4 ring-background shadow-lg shadow-primary/25" />
        </div>

        {/* Image side (opposite to content) */}
        <motion.div
          style={{ y: imageY }}
          className={`flex [direction:ltr] items-center ${isEven ? 'justify-start' : 'justify-end'}`}
        >
          {step.image?.asset ? (
            <motion.div
              className={cn(
                'overflow-hidden shadow-2xl shadow-primary/15 ring-1 ring-border/50',
                imageStyle === 'landscape'
                  ? 'w-full max-w-md aspect-video rounded-lg'
                  : 'w-56 h-56 lg:w-64 lg:h-64 rounded-full'
              )}
              style={{ filter: grayscaleFilter }}
            >
              <Image
                src={
                  imageStyle === 'landscape'
                    ? urlFor(step.image).width(800).height(450).quality(85).url()
                    : urlFor(step.image).width(600).height(600).quality(85).url()
                }
                alt={step.image?.alt || step.title}
                width={imageStyle === 'landscape' ? 800 : 600}
                height={imageStyle === 'landscape' ? 450 : 600}
                className="w-full h-full object-cover"
              />
            </motion.div>
          ) : (
            <div />
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}

export function WorkflowParallax({ section, pageSlug }: WorkflowParallaxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  const sortedSteps = useMemo(
    () => [...(section.workflowSteps || [])].sort((a, b) => a.stepNumber - b.stepNumber),
    [section.workflowSteps]
  );

  return (
    <section
      className={cn(
        'workflow-parallax py-16 md:py-24 lg:py-32 overflow-hidden',
        pageSlug && `workflow-parallax--${pageSlug}`
      )}
    >
      <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
        <div className="px-6 sm:px-0">
          {/* Header */}
          <div className="text-center mb-16 md:mb-24">
            {section.title && (
              <h2 className="font-medium mb-3 text-section-title leading-section-title">
                {section.title}
              </h2>
            )}
            {section.subtitle && (
              <p className="text-muted-foreground text-section-subtitle leading-section-subtitle max-w-2xl mx-auto">
                {section.subtitle}
              </p>
            )}
            {section.description && (
              <div className="prose prose-lg max-w-3xl mx-auto mt-4 text-left text-muted-foreground">
                <PortableText value={section.description} />
              </div>
            )}
          </div>

          {/* Steps with timeline */}
          <div ref={containerRef} className="relative max-w-5xl mx-auto">
            {/* Vertical timeline line — mobile: thin left line, desktop: centered */}
            <div className="absolute left-1.25 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px md:w-0.5 shadow-sm shadow-border/50">
              <div className="absolute inset-0 bg-border" />
              <motion.div
                className="absolute top-0 left-0 w-full bg-primary origin-top shadow-md shadow-primary/30"
                style={{ height: lineHeight }}
              />
            </div>

            {/* Steps */}
            <div className="space-y-6 md:space-y-0">
              {sortedSteps.map((step, index) => (
                <ParallaxStep
                  key={step._id}
                  step={step}
                  index={index}
                  imageStyle={section.imageStyle}
                />
              ))}
            </div>
          </div>

          {/* CTAs */}
          {section.ctas && section.ctas.length > 0 && (
            <div className="flex gap-4 justify-center mt-8 flex-wrap">
              {section.ctas.map((cta) => (
                <CTAButton key={cta._id} cta={cta} size="default" />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
